import { Component, Prop, h, Host, State } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-steps-line-type-1';

/**
 * Numbered sequential steps section for SignNow with colored circular badges.
 * Full-width centered heading above a set of steps: stacked vertically on mobile with a vertical connecting line, laid out in a row on desktop with a horizontal connecting line.
 * Unlike step-guide types which split heading and steps into side-by-side columns, this type centers the heading above a full-width step line.
 *
 * @category SignNow
 * @slot heading - {required}, <me-heading tag="h2"> - Section heading
 * @slot subheading - <me-text> - Optional section description text
 * @example
 *   <sn-steps-line-type-1
 *     items='[{"text":"Step one description."},{"text":"Step two description."}]'
 *   >
 *     <me-heading slot="heading" variant="3" tag="h2">Section title</me-heading>
 *   </sn-steps-line-type-1>
 */
@Component({
  tag: 'sn-steps-line-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNStepsLineType1 {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Numbered step items. Badge number and color are derived from item position automatically; up to 6 colors cycle.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} title - Optional step title displayed in bold before the text
   * @item.property {string} text - Step description text
   * @item.required text
   * @item.content title text
   * @minItems 1
   * @maxItems 6
   */
  @Prop({ mutable: true }) items?: { title?: string; text: string }[] | string;

  @State() expanded = false;

  componentWillLoad() {
    this.items = parseJsonProp(this.items);
  }

  render() {
    const items = Array.isArray(this.items) ? this.items : [];
    const collapsible = items.length > 3;
    const listClass = `${COMPONENT_NAME}__list${this.expanded ? ` ${COMPONENT_NAME}__list--expanded` : ''}`;

    return (
      <Host data-color-mode={this.colorMode}>
        <me-section class={COMPONENT_NAME} yGap="2xl" yGapLg="5xl">
          <div class={`${COMPONENT_NAME}__header`}>
            <slot name="heading"></slot>
            <slot name="subheading"></slot>
          </div>
          {items.length > 0 && (
            <div class={`${COMPONENT_NAME}__wrapper`}>
              <ol class={listClass}>
                {items.map((item, index) => (
                  <li class={`${COMPONENT_NAME}__item`} key={index}>
                    <div class={`${COMPONENT_NAME}__badge`} aria-hidden="true">
                      {index + 1}
                    </div>
                    <div class={`${COMPONENT_NAME}__text`}>
                      {item.title && <strong class={`${COMPONENT_NAME}__title`}>{item.title} </strong>}
                      {item.text}
                    </div>
                  </li>
                ))}
              </ol>
              {collapsible && (
                <button
                  class={`${COMPONENT_NAME}__read-more`}
                  aria-expanded={this.expanded ? 'true' : 'false'}
                  onClick={() => { this.expanded = !this.expanded; }}
                >
                  {this.expanded ? 'Read less' : 'Read more'}
                </button>
              )}
            </div>
          )}
        </me-section>
      </Host>
    );
  }
}
